import { Card, Text, Group, Badge, Stack, ThemeIcon } from '@mantine/core';
import { IconShield, IconAlertTriangle } from '@tabler/icons-react';

interface TopOffendingIP {
  ip: string;
  count: number;
}

interface TopOffendingIPsProps {
  data: TopOffendingIP[];
  maxItems?: number;
}

/**
 * Component to display top offending IP addresses with attempt counts
 * @param data - Array of IP addresses with their attempt counts
 * @param maxItems - Maximum number of items to display (default: 5)
 */
export function TopOffendingIPs({ data, maxItems = 5 }: TopOffendingIPsProps): JSX.Element {
  const displayData = data.slice(0, maxItems);

  if (displayData.length === 0) {
    return (
      <Card shadow="sm" padding="lg" radius="md" withBorder>
        <Group justify="space-between" mb="xs">
          <Text size="sm" fw={500} c="dimmed">
            Top Offending IPs
          </Text>
          <ThemeIcon color="green" variant="light" size="sm">
            <IconShield size={16} />
          </ThemeIcon>
        </Group>
        <Text size="sm" c="dimmed">
          No offending IPs found
        </Text>
      </Card>
    );
  }

  return (
    <Card shadow="sm" padding="lg" radius="md" withBorder>
      <Group justify="space-between" mb="md">
        <Text size="sm" fw={500} c="dimmed">
          Top Offending IPs
        </Text>
        <ThemeIcon color="red" variant="light" size="sm">
          <IconAlertTriangle size={16} />
        </ThemeIcon>
      </Group>
      
      <Stack gap="xs">
        {displayData.map((item, index) => (
          <Group key={item.ip} justify="space-between" p="xs" style={{ 
            backgroundColor: 'var(--mantine-color-gray-0)', 
            borderRadius: 'var(--mantine-radius-sm)' 
          }}>
            <Group gap="xs">
              <Badge size="sm" color="red" variant="light">
                #{index + 1}
              </Badge>
              <Text size="sm" fw={500} ff="monospace">
                {item.ip}
              </Text>
            </Group>
            <Badge color="red" variant="filled" size="sm">
              {item.count} attempts
            </Badge>
          </Group>
        ))}
      </Stack>
      
      {data.length > maxItems && (
        <Text size="xs" c="dimmed" mt="sm" ta="center">
          +{data.length - maxItems} more IPs
        </Text>
      )}
    </Card>
  );
}
